import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import NavLink from '../NavLink/navlink-component';

function HexagonOutline({ className }) {
    return (
        <svg viewBox="0 0 100 115" fill="none" xmlns="http://www.w3.org/2000/svg" className={className} aria-hidden="true">
            <polygon points="50,5 97,30 97,85 50,110 3,85 3,30" stroke="currentColor" strokeWidth="6" fill="none" />
        </svg>
    );
}

export default function CategoryListSection({ maxItems = 12, categories: propCategories, services: propServices }) {
    const {
        categories: reduxCategories = [],
        services: reduxServices = [],
        dir = 'rtl'
    } = useSelector((state) => state.systemData || {});

    const isRtl = dir === 'rtl';
    const source = propCategories ?? reduxCategories;
    const services = propServices ?? reduxServices;

    const items = useMemo(() => source.slice(0, maxItems), [source, maxItems]);

    const getCategoryServices = (category) => {
        const relatedServices = services.filter((service) => {
            return service.isActive !== false && service.categoryId === category.id;
        });

        if (relatedServices.length) {
            return relatedServices.map((service) => service.card?.title || service.title || service.name);
        }

        return category.items || [];
    };

    if (!items.length) return null;

    return (
        <section dir={dir} className="relative w-full bg-primary-50 py-12 px-4 overflow-hidden">
            <HexagonOutline className="absolute top-[-60px] right-[-30px] w-[170px] h-[170px] text-secondary-500 rotate-[18deg] opacity-80" />
            <HexagonOutline className="absolute bottom-[180px] left-[-50px] w-[140px] h-[140px] text-secondary-500 rotate-[18deg] opacity-80" />
            <HexagonOutline className="absolute bottom-[-70px] left-[50px] w-[210px] h-[210px] text-secondary-500 rotate-[18deg] opacity-80" />

            <div className="relative z-10 max-w-[1180px] mx-auto">
                <div className="flex overflow-x-auto snap-x snap-mandatory gap-4 pb-4 -mx-4 px-4 scrollbar-hide md:grid md:grid-cols-2 lg:grid-cols-3 md:gap-8 md:overflow-visible md:pb-0 md:mx-auto md:px-0">
                    {items.map((cat, index) => {
                        const categoryServices = getCategoryServices(cat);

                        return (
                            <NavLink
                                key={cat.id || cat.slug || index}
                                href={`/categories/${cat.slug}`}
                                title={cat.title || cat.name}
                                showUnderline={false}
                                className="flex-none w-[80vw] snap-start md:w-auto bg-white rounded-[32px] overflow-hidden border border-primary-100 shadow-sm flex flex-col min-h-[320px] group !opacity-100 !p-0"
                            >
                                <div className="relative bg-secondary-500 text-white px-6 pt-8 pb-12 rounded-b-[45%] min-h-[130px] flex items-center justify-center transition-colors duration-200 group-hover:bg-primary-500">
                                    <h3 className="font-secondary font-extralight text-xl md:text-2xl leading-tight text-center">
                                        {cat.title || cat.name}
                                    </h3>
                                </div>

                                <div className={`flex-1 flex flex-col justify-between gap-4 px-6 py-6 ${isRtl ? 'text-right' : 'text-left'}`}>
                                    {(cat.subtitle || cat.description) && (
                                        <p className="text-sm md:text-base text-primary-400 font-medium leading-relaxed line-clamp-3">
                                            {cat.subtitle || cat.description}
                                        </p>
                                    )}

                                    {!!categoryServices.length && (
                                        <ul className="space-y-2">
                                            {categoryServices.slice(0, 4).map((serviceTitle, serviceIndex) => (
                                                <li
                                                    key={`${cat.id}-service-${serviceIndex}`}
                                                    className={`flex gap-2 text-sm text-primary-500 font-medium leading-snug ${isRtl ? 'flex-row-reverse' : ''}`}
                                                >
                                                    <span className="mt-[6px] h-1.5 w-1.5 rounded-full bg-secondary-500 flex-none" />
                                                    <span>{serviceTitle}</span>
                                                </li>
                                            ))}
                                        </ul>
                                    )}

                                    <div className={`flex items-center gap-2 text-secondary-500 font-bold text-sm mt-auto ${isRtl ? 'flex-row-reverse justify-end' : ''}`}>
                                        <span>{cat.ctaText || (isRtl ? 'לצפייה בשירותים' : 'View services')}</span>
                                        <span className={`transition-transform duration-200 ${isRtl ? 'group-hover:-translate-x-1' : 'group-hover:translate-x-1'}`}>
                                            {isRtl ? '←' : '→'}
                                        </span>
                                    </div>
                                </div>
                            </NavLink>
                        );
                    })}
                </div>
            </div>
        </section>
    );
}